import { useEffect } from "react";
import { tamBaslik, kisaAciklama } from "@/lib/seo_metin";
import { genelBreadcrumbGerekli, semaSuz, semaTurleri } from "@/lib/ssr_sema";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schema?: Record<string, any>;
  noindex?: boolean;
  /** noindex sayfada bağlantılar izlensin: robots "noindex, follow" (SSR'de PageMeta.follow ile aynı) */
  follow?: boolean;
  /** og:title ve twitter:title; verilmezse başlık (markasızsa " | Dijipo" ekli). SSR'de PageMeta.sosyalBaslik ile aynı. */
  sosyalBaslik?: string;
}

// Türkiye hedefli varsayılan anahtar kelimeler - İç SEO & GEO optimizasyonu
const DEFAULT_KEYWORDS = [
  // Marka
  "dijipo", "dijipo ajans",
  // E-Ticaret (Ana Hizmet — Yüksek Dönüşümlü)
  "e-ticaret reklam ajansı", "eticaret reklam ajansı", "istanbul eticaret reklam ajansı",
  "e-ticaret dijital pazarlama ajansı", "e-ticaret performans pazarlama ajansı",
  "eticaret reklam yönetimi", "e-ticaret reklam yönetimi",
  "e-ticaret satış artırma ajansı", "e-ticaret danışmanlık ajansı",
  "e-ticaret danışmanlığı", "e-ticaret büyüme ajansı",
  "shopify ajansı", "shopify partner türkiye", "shopify reklam yönetimi",
  "shopify eticaret kurulum", "shopify e-ticaret kurulumu", "shopify eticaret reklam yönetimi",
  // Fiyat & Teklif Odaklı (Satın Alma Niyeti Yüksek)
  "e-ticaret reklam ajansı fiyatları", "instagram reklam danışmanlığı ücretleri",
  "e-ticaret reklam ajansı teklif", "e-ticaret performans pazarlama danışmanlığı",
  "e-ticaret roas artırma hizmeti",
  // Moda & Tekstil Dikeyi
  "kadın giyim markası reklam yönetimi", "giyim markası sosyal medya yönetimi",
  "moda markası dijital pazarlama ajansı", "tekstil e-ticaret reklam stratejileri",
  "butik e-ticaret reklam danışmanlığı", "giyim sektörü performans pazarlama",
  "moda e-ticaret reklam ajansı", "butik reklam ajansı", "tesettür giyim reklam ajansı",
  // Diğer Sektör Dikeyleri
  "kozmetik reklam ajansı", "kozmetik dijital pazarlama ajansı", "güzellik markası reklam ajansı",
  "takı reklam ajansı", "aksesuar reklam ajansı", "mücevher reklam ajansı",
  "ayakkabı e-ticaret reklam ajansı", "çanta markaları reklam yönetimi",
  "ev dekorasyon e-ticaret reklamları", "mobilya reklam ajansı",
  // Genel Ajans Varyantları
  "e-ticaret pazarlama ajansı", "e-ticaret performans ajansı", "performans reklam ajansı",
  "dijital reklam ajansı", "online reklam ajansı", "e-ticaret büyütme ajansı",
  "e-ticaret google ads ajansı", "e-ticaret meta reklam ajansı", "facebook instagram reklam ajansı",
  "e-ticaret dönüşüm optimizasyonu", "e-ticaret müşteri kazanma", "e-ticaret seo ajansı",
  "ikas ajansı", "ikas resmi iş ortağı", "ikas reklam yönetimi",
  // Dijital Pazarlama
  "dijital pazarlama ajansı", "performans pazarlaması", "dönüşüm optimizasyonu",
  "roas artırma", "roas odaklı reklam yönetimi",
  // Sosyal Medya - Şehir Hedefli
  "sosyal medya ajansı istanbul", "sosyal medya ajansı ankara", "sosyal medya ajansı izmir",
  "sosyal medya yönetimi", "sosyal medya reklam ajansı",
  // Instagram & Facebook
  "instagram reklam yönetimi", "instagram ajansı", "instagram reklam ajansı",
  "facebook reklam yönetimi", "facebook ads ajansı",
  // Google
  "google ads ajansı", "google reklam yönetimi", "google ads yönetimi",
  "google ads e-ticaret ajansı", "google alışveriş reklam yönetimi", "google shopping reklam yönetimi",
  // Platform Odaklı Danışmanlık
  "meta reklam ajansı", "facebook ve instagram reklam danışmanlığı",
  "sosyal medya reklam danışmanlığı", "tiktok e-ticaret reklam yönetimi",
  // TikTok
  "tiktok reklam ajansı", "tiktok ads yönetimi",
  // SEO
  "seo ajansı", "seo danışmanlığı", "teknik seo", "geo optimizasyonu",
  // Şehir Hedefli Diğer
  "dijital pazarlama ajansı istanbul", "dijital pazarlama ajansı ankara", "dijital pazarlama ajansı izmir",
  "reklam ajansı istanbul", "reklam ajansı ankara", "reklam ajansı izmir",
  "e-ticaret ajansı istanbul", "e-ticaret ajansı ankara", "e-ticaret ajansı izmir",
].join(", ");

export default function SEO({
  title,
  description,
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogType = "website",
  schema,
  noindex = false,
  follow = false,
  sosyalBaslik,
}: SEOProps) {
  useEffect(() => {
    // Sayfa başlığını güncelle
    document.title = tamBaslik(title);
    const metaAciklama = kisaAciklama(description);

    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [key, val] = attr.split("=");
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Temel Meta
    setMeta('meta[name="description"]', 'name=description', metaAciklama);
    setMeta('meta[name="keywords"]', 'name=keywords', keywords);
    // SSR (client/index.html, server/seo/render.ts) ile aynı değer: hidrasyondan sonra max-video-preview düşmesin.
    setMeta('meta[name="robots"]', 'name=robots', noindex ? (follow ? 'noindex, follow' : 'noindex, nofollow') : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Canonical
    const currentUrl = canonicalUrl || window.location.href.split('?')[0];
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", currentUrl);

    // Open Graph
    // Başlıkta marka zaten varsa ikinci kez eklenmez
    const sosyal = sosyalBaslik ?? (/dijipo/i.test(title) ? title : `${title} | Dijipo`);
    setMeta('meta[property="og:title"]', 'property=og:title', sosyal);
    setMeta('meta[property="og:description"]', 'property=og:description', metaAciklama);
    setMeta('meta[property="og:url"]', 'property=og:url', currentUrl);
    setMeta('meta[property="og:type"]', 'property=og:type', ogType);
    setMeta('meta[property="og:image"]', 'property=og:image', 'https://dijipo.com/dijipo-logo.png');

    // Twitter
    setMeta('meta[name="twitter:title"]', 'name=twitter:title', sosyal);
    setMeta('meta[name="twitter:description"]', 'name=twitter:description', metaAciklama);
    setMeta('meta[name="twitter:image"]', 'name=twitter:image', 'https://dijipo.com/dijipo-logo.png');

    // JSON-LD Şema Yapısı
    const existingSchemaScript = document.getElementById("json-ld-schema");
    if (existingSchemaScript) existingSchemaScript.remove();

    // Sunucu (server/seo/render.ts) bu yol için JSON-LD bastıysa (data-dj-ssr) aynı türler istemcide ikinci kez
    // eklenmez (ilk yükleme). SPA içinde başka sayfaya geçilince sunucu şemaları eski sayfaya aittir; silinir ve
    // istemci şemaları eklenir (client/src/lib/ssr_sema.ts).
    let buSayfa = window.location.pathname.replace(/\/+$/, "") || "/";
    try { buSayfa = decodeURIComponent(buSayfa); } catch { /* geçersiz kodlama: olduğu gibi */ }
    const ssrSema = Array.from(document.querySelectorAll<HTMLScriptElement>("script[data-dj-ssr]"));
    ssrSema.filter(s => s.dataset.djSsr !== buSayfa).forEach(s => s.remove());
    const ssrTurleri = semaTurleri(ssrSema.filter(s => s.dataset.djSsr === buSayfa).map(s => s.textContent ?? ""));
    // Şema verilmeyen sayfa ek JSON-LD basmaz: kurum (#organization) ve yerel işletme (#localbusiness)
    // client/index.html'de her sayfada tek kez tanımlı; burada ikinci bir işletme varlığı üretilmez (P15).
    const schemaData = schema ? semaSuz(schema, ssrTurleri) : null;
    if (schemaData) {
      const script = document.createElement("script");
      script.id = "json-ld-schema";
      script.type = "application/ld+json";
      script.innerHTML = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }

    // BreadcrumbList şeması (iç SEO)
    const existingBreadcrumb = document.getElementById("json-ld-breadcrumb");
    if (existingBreadcrumb) existingBreadcrumb.remove();

    // SSR'de (ilk yükleme) ya da sayfanın kendi şemasında (SPA geçişi) BreadcrumbList varsa genel olanı eklenmez.
    // noindex sayfalarda (404, /dosyalar, /admin) SSR BreadcrumbList basmaz; istemci de eklemez.
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    if (!noindex && genelBreadcrumbGerekli(pathSegments.length, ssrTurleri, schema)) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://dijipo.com" },
          ...pathSegments.map((segment, index) => ({
            "@type": "ListItem",
            "position": index + 2,
            "name": formatBreadcrumb(segment),
            "item": `https://dijipo.com/${pathSegments.slice(0, index + 1).join('/')}`
          }))
        ]
      };
      const bcScript = document.createElement("script");
      bcScript.id = "json-ld-breadcrumb";
      bcScript.type = "application/ld+json";
      bcScript.innerHTML = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(bcScript);
    }
  }, [title, description, keywords, canonicalUrl, ogType, schema, noindex, follow, sosyalBaslik]);

  return null;
}

function formatBreadcrumb(slug: string): string {
  const map: Record<string, string> = {
    'kurumsal': 'Kurumsal',
    'hizmetlerimiz': 'Hizmetlerimiz',
    'referanslar': 'Referanslar',
    'fiyatlar': 'Fiyatlar',
    'sss': 'Sıkça Sorulan Sorular',
    'blog': 'Blog',
    'sozluk': 'Dijital Sözlük',
    'roi-hesaplayici': 'ROI Hesaplayıcı',
    'randevu': 'Randevu',
    'gelistirmelerimiz': 'Geliştirmelerimiz',
    'facebook-reklam-hesabi-kiralama': 'Facebook Reklam Hesabı Kiralama',
  };
  return map[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
